import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import { Separator } from '@repo/ui/components/ui/separator'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components/ui/tooltip'
import { cn } from '@repo/ui/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import type { FeatureCollection, Geometry } from 'geojson'
import type { LucideIcon } from 'lucide-react'
import {
  CalendarRange,
  Check,
  ChevronRight,
  Clock,
  ListFilter,
  MapPinned,
  MessageSquare,
  Phone,
  Route as RouteIcon,
  User,
  X,
} from 'lucide-react'
import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { WorkspaceRouteShell } from '@/components/app/route-shells'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import {
  AppWorkspaceHeader,
  AppWorkspaceMainSupportingSplit,
  AppWorkspacePane,
  AppWorkspacePaneHeader,
  AppWorkspacePanel,
  WorkspaceMetricStrip,
} from '@/components/app/workspace-shell'
import { ImportedEventPanel } from '@/components/calendar/imported-event-panel'
import {
  formatCalendarTime,
  getCalendarEventDateKey,
  getCalendarEventTime,
  getCalendarStateLabel,
} from '@/lib/calendar/calendar-status'
import type { ImportedCalendarEvent } from '@/lib/calendar/types'
import { formatPhoneDisplay } from '@/lib/format-phone'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute('/_app/appointments')({
  component: AppointmentsPage,
  staticData: {
    breadcrumbs: [{ label: 'Appointments' }],
  } satisfies RouteStaticData,
})

type AppointmentStatus = 'confirmed' | 'cancelled' | 'completed'

type Appointment = {
  id: string
  date: string
  startTime: string
  endTime: string
  status: string
  durationMinutes: number
  quantity: number
  notes: string | null
  isFlexible: boolean | null
  addressText: string | null
  lat: string | null
  lng: string | null
  h3Index: string | null
  formResponses: Record<string, unknown> | null
  customer: {
    name: string
    phone: string
    email: string | null
  } | null
  service: {
    name: string
  } | null
}

type ZctaCoverageCandidate = {
  postalCode: string
  geometry: Geometry
}

function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function formatDisplayDate(dateKey: string): string {
  if (!dateKey) return 'No date'
  const [year, month, day] = dateKey.split('-').map(Number)
  if (!year || !month || !day) return dateKey
  const date = new Date(year, month - 1, day)
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function todayKey(): string {
  return formatDateKey(new Date())
}

function parsePostalCodeList(value: string | null | undefined) {
  if (!value) return []

  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return []
    return [...new Set(parsed.filter((item): item is string => /^\d{5}$/.test(item)))].sort()
  } catch {
    return []
  }
}

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-success/12 text-success dark:bg-success/16 dark:text-success',
  cancelled: 'bg-destructive/12 text-destructive dark:bg-destructive/16 dark:text-destructive',
  completed: 'bg-route/12 text-route dark:bg-route/16 dark:text-route',
}

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All', icon: ListFilter },
  { value: 'confirmed', label: 'Confirmed', icon: Check },
  { value: 'cancelled', label: 'Cancelled', icon: X },
  { value: 'completed', label: 'Completed', icon: Clock },
] as const

const DayRouteMap = lazy(() =>
  import('@/components/schedule/DayRouteMap').then((m) => ({ default: m.DayRouteMap }))
)

function AppointmentsPage() {
  const utils = trpc.useUtils()
  const { configuration } = useOperatorConfiguration()
  const today = todayKey()
  const tomorrow = formatDateKey(addDays(new Date(), 1))

  const [dateFrom, setDateFrom] = useState(today)
  const [dateTo, setDateTo] = useState(today)
  const [statusFilter, setStatusFilter] = useState<string>('confirmed')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: appointments = [], isLoading } = trpc.appointments.list.useQuery({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    status: (statusFilter as AppointmentStatus) || undefined,
  })
  const { data: allRangeAppointments = [] } = trpc.appointments.list.useQuery({
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  })
  const { data: importedEvents = [], isLoading: importedEventsLoading } =
    trpc.calendar.events.list.useQuery({
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      managementState:
        statusFilter === 'cancelled' || statusFilter === 'completed' ? undefined : 'needs_details',
    })

  const cancelMutation = trpc.appointments.cancel.useMutation({
    onSuccess: () => {
      utils.appointments.list.invalidate()
      utils.calendar.events.list.invalidate()
      setSelectedId(null)
    },
  })

  const completeMutation = trpc.appointments.complete.useMutation({
    onSuccess: () => {
      utils.appointments.list.invalidate()
      utils.calendar.events.list.invalidate()
      setSelectedId(null)
    },
  })
  const updateMutation = trpc.appointments.update.useMutation({
    onSuccess: () => {
      utils.appointments.list.invalidate()
      utils.calendar.events.list.invalidate()
    },
  })

  const isSingleDay = !!dateFrom && dateFrom === dateTo
  const typedAppointments = appointments as Appointment[]
  const typedAllRangeAppointments = allRangeAppointments as Appointment[]

  const sortedAppointments = useMemo(
    () =>
      [...typedAppointments].sort(
        (left, right) =>
          left.date.localeCompare(right.date) || left.startTime.localeCompare(right.startTime)
      ),
    [typedAppointments]
  )
  const typedImportedEvents = importedEvents as ImportedCalendarEvent[]
  const visibleImportedEvents = useMemo(() => {
    if (statusFilter === 'cancelled' || statusFilter === 'completed') return []
    return typedImportedEvents
      .filter((event) => !event.appointmentId && event.managementState === 'needs_details')
      .sort((left, right) => new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime())
  }, [typedImportedEvents, statusFilter])
  const timelineItems = useMemo(() => {
    const appointmentItems = sortedAppointments.map((appointment) => ({
      type: 'appointment' as const,
      key: `appointment:${appointment.id}`,
      sortDate: appointment.date,
      sortTime: appointment.startTime,
      appointment,
    }))
    const importedItems = visibleImportedEvents.map((event) => ({
      type: 'imported' as const,
      key: `imported:${event.id}`,
      sortDate: getCalendarEventDateKey(event.startsAt),
      sortTime: getCalendarEventTime(event.startsAt),
      event,
    }))

    return [...appointmentItems, ...importedItems].sort(
      (left, right) =>
        left.sortDate.localeCompare(right.sortDate) || left.sortTime.localeCompare(right.sortTime)
    )
  }, [sortedAppointments, visibleImportedEvents])

  const selectedItem =
    timelineItems.find((item) => item.key === selectedId) ?? timelineItems[0] ?? null
  const selectedAppointment = selectedItem?.type === 'appointment' ? selectedItem.appointment : null
  const selectedImportedEvent = selectedItem?.type === 'imported' ? selectedItem.event : null

  const confirmedAppointments = useMemo(
    () => sortedAppointments.filter((appointment) => appointment.status === 'confirmed'),
    [sortedAppointments]
  )

  const mappedAppointments = useMemo(
    () =>
      confirmedAppointments.filter((appointment) => {
        if (!appointment.lat || !appointment.lng) return false
        return Number.isFinite(Number(appointment.lat)) && Number.isFinite(Number(appointment.lng))
      }),
    [confirmedAppointments]
  )
  const routeCoverageCenter = useMemo(() => {
    const area = configuration.serviceArea
    if (!area?.centerLat || !area.centerLng) return null

    const centerLat = Number(area.centerLat)
    const centerLng = Number(area.centerLng)
    if (!Number.isFinite(centerLat) || !Number.isFinite(centerLng)) return null

    return { centerLat, centerLng, radiusMiles: area.softRadiusMiles || 10 }
  }, [configuration.serviceArea])
  const routeCoverageQuery = trpc.serviceArea.previewZctaCoverage.useQuery(
    {
      centerLat: routeCoverageCenter?.centerLat ?? 0,
      centerLng: routeCoverageCenter?.centerLng ?? 0,
      radiusMiles: routeCoverageCenter?.radiusMiles ?? 10,
      overlapThreshold: 0.4,
    },
    {
      enabled: Boolean(routeCoverageCenter),
      retry: false,
      staleTime: 1000 * 60 * 10,
    }
  )
  const routeCoverageData = useMemo<FeatureCollection<Geometry, object> | null>(() => {
    const candidates = (routeCoverageQuery.data ?? []) as ZctaCoverageCandidate[]
    if (candidates.length === 0) return null

    const includedPostalCodes = parsePostalCodeList(
      configuration.serviceArea?.includedPostalCodesJson
    )
    const excludedPostalCodes = parsePostalCodeList(
      configuration.serviceArea?.excludedPostalCodesJson
    )
    const visibleCoverage = candidates.filter((candidate) => {
      if (excludedPostalCodes.includes(candidate.postalCode)) return false
      if (includedPostalCodes.length > 0) return includedPostalCodes.includes(candidate.postalCode)
      return true
    })

    if (visibleCoverage.length === 0) return null

    return {
      type: 'FeatureCollection',
      features: visibleCoverage.map((candidate) => ({
        type: 'Feature',
        geometry: candidate.geometry,
        properties: {
          postalCode: candidate.postalCode,
        },
      })),
    }
  }, [
    configuration.serviceArea?.excludedPostalCodesJson,
    configuration.serviceArea?.includedPostalCodesJson,
    routeCoverageQuery.data,
  ])

  const travelPairs = useMemo(() => {
    if (!isSingleDay || confirmedAppointments.length < 2) return []
    const geoStops = confirmedAppointments.filter((appointment) => appointment.h3Index)
    const pairs: Array<{ fromH3: string; toH3: string }> = []
    for (let i = 0; i < geoStops.length - 1; i++) {
      const from = geoStops[i].h3Index
      const to = geoStops[i + 1].h3Index
      if (from && to) pairs.push({ fromH3: from, toH3: to })
    }
    return pairs
  }, [confirmedAppointments, isSingleDay])

  const travelTimeQuery = trpc.geo.batchTravelTime.useQuery(
    { pairs: travelPairs },
    { enabled: travelPairs.length > 0 }
  )

  const totalDriveTime = useMemo(() => {
    if (!travelTimeQuery.data) return 0
    return travelTimeQuery.data.reduce((sum, result) => sum + result.durationMinutes, 0)
  }, [travelTimeQuery.data])

  const statusCounts = useMemo(() => {
    return typedAllRangeAppointments.reduce(
      (counts, appointment) => {
        counts[appointment.status] = (counts[appointment.status] ?? 0) + 1
        return counts
      },
      {} as Record<string, number>
    )
  }, [typedAllRangeAppointments])
  const allRangeAppointmentCount = typedAllRangeAppointments.length

  const rangeLabel = isSingleDay
    ? formatDisplayDate(dateFrom)
    : `${formatDisplayDate(dateFrom)} - ${formatDisplayDate(dateTo)}`
  const activeRangePreset =
    dateFrom === today && dateTo === today
      ? 'today'
      : dateFrom === tomorrow && dateTo === tomorrow
        ? 'tomorrow'
        : dateFrom === today && dateTo === formatDateKey(addDays(new Date(), 6))
          ? 'week'
          : 'custom'

  const routeLabel =
    totalDriveTime > 0
      ? `${totalDriveTime} min drive`
      : confirmedAppointments.length > 1
        ? 'Drive time unavailable'
        : confirmedAppointments.length === 1
          ? 'Single stop'
          : 'No route yet'

  const showToday = () => {
    setDateFrom(today)
    setDateTo(today)
    setStatusFilter('confirmed')
  }

  const showTomorrow = () => {
    setDateFrom(tomorrow)
    setDateTo(tomorrow)
    setStatusFilter('confirmed')
  }

  const showNextSevenDays = () => {
    const now = new Date()
    setDateFrom(formatDateKey(now))
    setDateTo(formatDateKey(addDays(now, 6)))
    setStatusFilter('')
  }

  return (
    <WorkspaceRouteShell>
      <AppTopBarActions>
        <AppTopBarActionGroup>
          <Button
            variant={activeRangePreset === 'today' ? 'default' : 'outline'}
            size="sm"
            className="cursor-pointer gap-2"
            onClick={showToday}
          >
            <RouteIcon className="h-4 w-4" />
            Today
          </Button>
          <Button
            variant={activeRangePreset === 'tomorrow' ? 'default' : 'outline'}
            size="sm"
            className="cursor-pointer"
            onClick={showTomorrow}
          >
            Tomorrow
          </Button>
          <Button
            variant={activeRangePreset === 'week' ? 'default' : 'outline'}
            size="sm"
            className="cursor-pointer gap-2"
            onClick={showNextSevenDays}
          >
            <CalendarRange className="h-4 w-4" />7 days
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>
      <AppWorkspacePanel>
        <AppWorkspaceHeader>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
            <div>
              <div className="app-section-label">Dispatch board</div>
              <h2 className="mt-1.5 font-sans text-xl font-semibold tracking-tight text-foreground md:text-2xl">
                {confirmedAppointments.length === 0
                  ? 'No confirmed route in view'
                  : `${confirmedAppointments.length} confirmed ${confirmedAppointments.length === 1 ? 'job' : 'jobs'}`}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {rangeLabel} · {routeLabel} · {mappedAppointments.length} mapped
              </p>
            </div>
            <WorkspaceMetricStrip
              className="sm:grid-cols-3"
              metrics={[
                {
                  key: 'stops',
                  label: 'Stops',
                  value: String(confirmedAppointments.length),
                  detail: 'confirmed',
                  icon: <RouteIcon className="h-4 w-4" />,
                },
                {
                  key: 'mapped',
                  label: 'Mapped',
                  value: String(mappedAppointments.length),
                  detail: 'mapped',
                  icon: <MapPinned className="h-4 w-4" />,
                },
                {
                  key: 'drive',
                  label: 'Drive',
                  value: totalDriveTime > 0 ? `${totalDriveTime}m` : 'Pending',
                  detail: confirmedAppointments.length > 1 ? 'estimated' : 'single stop',
                  icon: <Clock className="h-4 w-4" />,
                },
              ]}
            />
          </div>
        </AppWorkspaceHeader>

        <AppWorkspaceMainSupportingSplit>
          <AppWorkspacePane surface="region">
            <div className="flex h-full flex-col">
              <div className="app-workspace-header border-b border-border/70 px-4 py-3">
                <AppWorkspacePaneHeader
                  eyebrow="Route window"
                  actions={
                    <Badge variant="outline" className="rounded-[4px]">
                      {routeLabel}
                    </Badge>
                  }
                  className="min-h-[2rem] border-b-0 px-0 py-0 md:px-0"
                />
                <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                  <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-2">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(event) => setDateFrom(event.target.value)}
                      className="h-8 rounded-[4px] bg-background/85 text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-[2.5rem_minmax(0,1fr)] items-center gap-2">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(event) => setDateTo(event.target.value)}
                      className="h-8 rounded-[4px] bg-background/85 text-sm"
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <Tooltip key={option.value || 'all'}>
                      <TooltipTrigger asChild>
                        <Button
                          variant={statusFilter === option.value ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 cursor-pointer gap-1.5 rounded-[4px] px-3 text-xs"
                          onClick={() => setStatusFilter(option.value)}
                          aria-label={`Filter by ${option.label}`}
                        >
                          <option.icon className="h-3.5 w-3.5" />
                          <span>
                            {option.label}
                            {option.value
                              ? ` ${statusCounts[option.value] ?? 0}`
                              : ` ${allRangeAppointmentCount}`}
                          </span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" sideOffset={6}>
                        {option.label}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
                {isLoading || importedEventsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="app-data-card h-24 animate-pulse rounded-[6px]" />
                    ))}
                  </div>
                ) : timelineItems.length === 0 ? (
                  <div className="flex min-h-0 flex-1">
                    <EmptyRouteTimeline
                      rangeLabel={rangeLabel}
                      hasFilters={statusFilter !== 'confirmed' || !isSingleDay}
                    />
                  </div>
                ) : (
                  <div className="relative border-l border-route/35 pl-5">
                    <div className="space-y-4">
                      {timelineItems.map((item, index) => {
                        const isSelected = selectedItem?.key === item.key
                        if (item.type === 'imported') {
                          const event = item.event
                          return (
                            <button
                              key={item.key}
                              type="button"
                              className={cn(
                                'app-timeline-card group relative grid w-full cursor-pointer grid-cols-[4.25rem_minmax(0,1fr)] gap-4 rounded-[6px] border-dashed border-primary/35 bg-primary/5 p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                isSelected &&
                                  'border-primary/70 bg-primary/10 ring-1 ring-primary/30'
                              )}
                              onClick={() => setSelectedId(item.key)}
                            >
                              <span
                                className={cn(
                                  '-left-[1.72rem] absolute top-5 h-3 w-3 rounded-full border-2 bg-background',
                                  isSelected ? 'border-primary' : 'border-primary/55'
                                )}
                              />
                              <div className="min-w-0">
                                <span className="font-mono text-sm font-semibold text-foreground">
                                  {formatCalendarTime(event.startsAt)}
                                </span>
                                <div className="mt-1 text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground">
                                  {formatDisplayDate(item.sortDate).split(',')[0]}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="flex min-w-0 items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <p className="truncate font-medium text-foreground">
                                      {event.title}
                                    </p>
                                    <p className="mt-1 truncate text-sm text-muted-foreground">
                                      Imported calendar event
                                    </p>
                                  </div>
                                  <ChevronRight
                                    className={cn(
                                      'mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5',
                                      isSelected && 'text-primary'
                                    )}
                                  />
                                </div>
                                <div className="mt-3 flex flex-wrap items-center gap-2">
                                  <Badge variant="outline" className="rounded-[4px]">
                                    {getCalendarStateLabel(event)}
                                  </Badge>
                                  {event.busy ? (
                                    <Badge variant="secondary" className="rounded-[4px]">
                                      Blocks booking
                                    </Badge>
                                  ) : null}
                                </div>
                                {event.locationText ? (
                                  <div className="mt-3 flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
                                    <MapPinned className="mt-0.5 h-3.5 w-3.5 shrink-0 text-route" />
                                    <span className="line-clamp-2">{event.locationText}</span>
                                  </div>
                                ) : null}
                              </div>
                            </button>
                          )
                        }
                        const appointment = item.appointment
                        return (
                          <button
                            key={item.key}
                            type="button"
                            className={cn(
                              'app-timeline-card group relative grid w-full cursor-pointer grid-cols-[4.25rem_minmax(0,1fr)] gap-4 rounded-[6px] p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              isSelected && 'border-primary/55 bg-primary/5 ring-1 ring-primary/30'
                            )}
                            onClick={() => setSelectedId(item.key)}
                          >
                            <span
                              className={cn(
                                '-left-[1.72rem] absolute top-5 h-3 w-3 rounded-full border-2 bg-background',
                                isSelected ? 'border-primary' : 'border-route/70'
                              )}
                            />
                            <div className="min-w-0">
                              <span className="font-mono text-sm font-semibold text-foreground">
                                {appointment.startTime}
                              </span>
                              <div className="mt-1 text-[0.65rem] uppercase tracking-[0.18em] text-muted-foreground">
                                {formatDisplayDate(appointment.date).split(',')[0]}
                              </div>
                              {index === 0 && activeRangePreset === 'today' ? (
                                <Badge variant="outline" className="mt-3 rounded-[4px]">
                                  Next
                                </Badge>
                              ) : null}
                            </div>

                            <div className="min-w-0">
                              <div className="flex min-w-0 items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate font-medium text-foreground">
                                    {appointment.service?.name || 'Service'}
                                  </p>
                                  <p className="mt-1 truncate text-sm text-muted-foreground">
                                    {appointment.customer?.name || 'Customer pending'}
                                  </p>
                                </div>
                                <ChevronRight
                                  className={cn(
                                    'mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5',
                                    isSelected && 'text-primary'
                                  )}
                                />
                              </div>

                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                <Badge
                                  className={cn(
                                    'rounded-[4px]',
                                    STATUS_COLORS[appointment.status] || ''
                                  )}
                                >
                                  {appointment.status}
                                </Badge>
                                {appointment.isFlexible ? (
                                  <Badge variant="outline" className="rounded-[4px]">
                                    Flexible
                                  </Badge>
                                ) : null}
                              </div>
                              {appointment.addressText ? (
                                <div className="mt-3 flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
                                  <MapPinned className="mt-0.5 h-3.5 w-3.5 shrink-0 text-route" />
                                  <span className="line-clamp-2">{appointment.addressText}</span>
                                </div>
                              ) : null}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </AppWorkspacePane>

          <AppWorkspacePane className="grid grid-rows-[minmax(22rem,1.08fr)_minmax(13rem,0.72fr)] divide-y divide-border/70">
            <div className="app-workspace-muted min-h-0">
              <div className="flex h-full flex-col">
                <AppWorkspacePaneHeader
                  eyebrow="Route map"
                  description="Local coverage area; route stops appear when booked."
                  actions={
                    <Badge variant="outline" className="rounded-[4px]">
                      {mappedAppointments.length} stops
                    </Badge>
                  }
                  className="min-h-[3.5rem] px-4 py-3 md:px-4"
                />
                <div className="min-h-0 flex-1 p-4">
                  <Suspense
                    fallback={
                      <div className="flex h-full min-h-[18rem] items-center justify-center rounded-[6px] border border-border/70 text-sm text-muted-foreground">
                        Loading map...
                      </div>
                    }
                  >
                    <DayRouteMap
                      appointments={confirmedAppointments}
                      coverageData={routeCoverageData}
                      selectedId={selectedAppointment?.id}
                      onSelectAppointment={(id) => setSelectedId(id ? `appointment:${id}` : null)}
                    />
                  </Suspense>
                </div>
              </div>
            </div>

            {selectedImportedEvent ? (
              <ImportedEventPanel
                event={selectedImportedEvent}
                onConverted={() => setSelectedId(null)}
              />
            ) : (
              <AppointmentDetails
                appointment={selectedAppointment}
                completePending={completeMutation.isPending}
                cancelPending={cancelMutation.isPending}
                reschedulePending={updateMutation.isPending}
                rescheduleError={updateMutation.error?.message ?? null}
                onComplete={(id) => completeMutation.mutate({ id })}
                onCancel={(id) => cancelMutation.mutate({ id })}
                onReschedule={(input) => updateMutation.mutate(input)}
              />
            )}
          </AppWorkspacePane>
        </AppWorkspaceMainSupportingSplit>
      </AppWorkspacePanel>
    </WorkspaceRouteShell>
  )
}

function EmptyRouteTimeline({
  rangeLabel,
  hasFilters,
}: {
  rangeLabel: string
  hasFilters: boolean
}) {
  const timeMarkers = ['8a', '10a', '12p', '2p', '4p', '6p']

  return (
    <div className="app-empty-surface relative flex min-h-[24rem] flex-1 overflow-hidden">
      <div className="absolute inset-0 opacity-70 [background-image:linear-gradient(color-mix(in_oklab,var(--border)_55%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_oklab,var(--border)_45%,transparent)_1px,transparent_1px)] [background-size:48px_48px]" />
      <div className="relative z-10 flex min-h-[24rem] flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="app-section-label">Timeline clear</div>
            <div className="mt-1.5 font-sans text-lg font-semibold tracking-tight text-foreground">
              No jobs scheduled
            </div>
            <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">
              {hasFilters
                ? 'This route window has no matching appointments.'
                : 'Confirmed appointments will stack here in the order the operator should run the day.'}
            </p>
          </div>
          <Badge variant="outline" className="rounded-[4px]">
            {rangeLabel}
          </Badge>
        </div>

        <div className="mt-5 flex min-h-0 flex-1">
          <div className="flex w-14 flex-col justify-between py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {timeMarkers.map((marker) => (
              <span key={marker}>{marker}</span>
            ))}
          </div>
          <div className="relative flex-1 border-l border-route/35 pl-6">
            <div className="absolute top-2 bottom-2 left-[-5px] flex flex-col justify-between">
              {timeMarkers.map((marker) => (
                <span
                  key={marker}
                  className="h-2.5 w-2.5 rounded-full border border-route/50 bg-background"
                />
              ))}
            </div>
            <div className="flex h-full items-center py-4">
              <div className="app-empty-surface w-full px-4 py-5">
                <div className="flex items-center gap-4">
                  <div className="app-icon-tile h-9 w-9 text-route">
                    <RouteIcon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Route not built yet</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Add or confirm bookings to create stops.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AppointmentDetails({
  appointment,
  completePending,
  cancelPending,
  reschedulePending,
  rescheduleError,
  onComplete,
  onCancel,
  onReschedule,
}: {
  appointment: Appointment | null
  completePending: boolean
  cancelPending: boolean
  reschedulePending: boolean
  rescheduleError: string | null
  onComplete: (id: string) => void
  onCancel: (id: string) => void
  onReschedule: (input: { id: string; date: string; startTime: string }) => void
}) {
  const [draftDate, setDraftDate] = useState('')
  const [draftStartTime, setDraftStartTime] = useState('')

  useEffect(() => {
    setDraftDate(appointment?.date ?? '')
    setDraftStartTime(appointment?.startTime ?? '')
  }, [appointment?.date, appointment?.startTime])

  if (!appointment) {
    return (
      <div className="flex min-h-0 bg-background/35 p-4">
        <div className="app-subpanel grid min-h-[10.5rem] flex-1 gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(14rem,0.72fr)] lg:items-center">
          <div className="flex items-center gap-4">
            <div className="app-icon-tile h-12 w-12 shrink-0 text-primary">
              <User className="h-5 w-5" />
            </div>
            <div>
              <div className="app-section-label">Inspector</div>
              <p className="mt-2 font-sans text-xl font-semibold tracking-tight text-foreground">
                No appointment selected
              </p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                Select a job to view customer contact, notes, form answers, and field actions.
              </p>
            </div>
          </div>
          <div className="app-data-card grid gap-0 overflow-hidden rounded-[6px] sm:grid-cols-3 lg:grid-cols-1">
            {[
              { label: 'Customer', value: 'Pending' },
              { label: 'Address', value: 'Pending' },
              { label: 'Actions', value: 'Hidden' },
            ].map((item) => (
              <div
                key={item.label}
                className="border-border/70 border-b px-3 py-2.5 last:border-b-0 sm:border-r sm:border-b-0 sm:last:border-r-0 lg:border-r-0 lg:border-b lg:last:border-b-0"
              >
                <div className="app-section-label">{item.label}</div>
                <div className="mt-1 text-sm font-medium text-foreground">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-0 overflow-y-auto bg-background/35 p-4 md:p-5">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="app-section-label">Appointment</div>
            <h3 className="mt-3 font-sans text-xl font-semibold tracking-tight text-foreground">
              {appointment.service?.name || 'Service'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {appointment.date} · {appointment.startTime} - {appointment.endTime}
            </p>
          </div>
          <Badge className={cn('rounded-[4px]', STATUS_COLORS[appointment.status] || '')}>
            {appointment.status}
          </Badge>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailTile icon={Clock} label="Duration" value={`${appointment.durationMinutes} min`} />
          <DetailTile icon={User} label="Quantity" value={String(appointment.quantity ?? 1)} />
        </div>

        <Separator />

        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Customer</p>
          {appointment.customer ? (
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 text-foreground">
                <User className="h-4 w-4 text-muted-foreground" />
                {appointment.customer.name}
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {formatPhoneDisplay(appointment.customer.phone)}
              </div>
              {appointment.customer.email ? <p>{appointment.customer.email}</p> : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No customer info</p>
          )}
        </div>

        {appointment.customer?.phone || appointment.addressText ? (
          <div className="flex flex-wrap gap-2">
            {appointment.customer?.phone ? (
              <>
                <Button asChild variant="outline" size="sm" className="cursor-pointer">
                  <a href={`tel:${appointment.customer.phone}`}>
                    <Phone className="mr-1 h-3.5 w-3.5" />
                    Call
                  </a>
                </Button>
                <Button asChild variant="outline" size="sm" className="cursor-pointer">
                  <a href={`sms:${appointment.customer.phone}`}>
                    <MessageSquare className="mr-1 h-3.5 w-3.5" />
                    Text
                  </a>
                </Button>
              </>
            ) : null}
            {appointment.addressText ? (
              <Button asChild variant="outline" size="sm" className="cursor-pointer">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(appointment.addressText)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MapPinned className="mr-1 h-3.5 w-3.5" />
                  Directions
                </a>
              </Button>
            ) : null}
          </div>
        ) : null}

        {appointment.notes ? (
          <>
            <Separator />
            <div className="text-sm">
              <p className="font-medium text-foreground">Notes</p>
              <p className="mt-1 text-muted-foreground">{appointment.notes}</p>
            </div>
          </>
        ) : null}

        {appointment.formResponses && Object.keys(appointment.formResponses).length > 0 ? (
          <>
            <Separator />
            <div className="text-sm">
              <p className="font-medium text-foreground">Questionnaire</p>
              <div className="mt-2 space-y-1">
                {Object.entries(appointment.formResponses).map(([key, value]) => (
                  <p key={key} className="text-muted-foreground">
                    <span className="font-medium text-foreground">{key}:</span> {String(value)}
                  </p>
                ))}
              </div>
            </div>
          </>
        ) : null}

        {appointment.status === 'confirmed' ? (
          <>
            <Separator />
            <div className="space-y-3 rounded-[6px] border border-border/70 bg-card/45 p-3">
              <div>
                <p className="text-sm font-medium text-foreground">Reschedule</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Move this appointment after confirming the new time with the customer.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor={`appointment-date-${appointment.id}`}>Date</Label>
                  <Input
                    id={`appointment-date-${appointment.id}`}
                    type="date"
                    value={draftDate}
                    onChange={(event) => setDraftDate(event.target.value)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`appointment-time-${appointment.id}`}>Start time</Label>
                  <Input
                    id={`appointment-time-${appointment.id}`}
                    type="time"
                    value={draftStartTime}
                    onChange={(event) => setDraftStartTime(event.target.value)}
                    className="h-9"
                  />
                </div>
              </div>
              {rescheduleError ? (
                <p className="text-sm text-destructive">{rescheduleError}</p>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                disabled={
                  reschedulePending ||
                  !draftDate ||
                  !draftStartTime ||
                  (draftDate === appointment.date && draftStartTime === appointment.startTime)
                }
                onClick={() =>
                  onReschedule({
                    id: appointment.id,
                    date: draftDate,
                    startTime: draftStartTime,
                  })
                }
              >
                <CalendarRange className="mr-1 h-3.5 w-3.5" />
                {reschedulePending ? 'Saving...' : 'Save new time'}
              </Button>
            </div>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                className="cursor-pointer"
                onClick={() => onComplete(appointment.id)}
                disabled={completePending}
              >
                <Check className="mr-1 h-3.5 w-3.5" />
                Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => onCancel(appointment.id)}
                disabled={cancelPending}
              >
                <X className="mr-1 h-3.5 w-3.5" />
                Cancel
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

function DetailTile({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon
  label: string
  value: string
}) {
  return (
    <div className="app-subpanel px-3 py-2.5">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 font-medium text-foreground">{value}</div>
    </div>
  )
}
